/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography
} from '@mui/material';
import { FormData } from './types';

interface EditPromptDialogProps {
  open: boolean;
  onClose: () => void;
  onBack: () => void;
  onGenerate: () => void;
  prompt: string;
  onPromptChange: (newPrompt: string) => void;
}

const EditPromptDialog: React.FC<EditPromptDialogProps> = ({
  open,
  onClose,
  onBack,
  onGenerate,
  prompt,
  onPromptChange
}) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit AI Prompt</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Review and edit the prompt that will be sent to generate your script. This prompt includes all your settings, characters, and tropes.
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={20}
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          variant="outlined"
          sx={{ mb: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 2, pb: 1 }}>
          <Button onClick={onBack} color="primary">
            Back
          </Button>
          <Box>
            <Button onClick={onClose} color="primary" sx={{ mr: 1 }}>
              Save
            </Button>
            <Button onClick={onGenerate} variant="contained" color="primary">
              Generate Script
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditPromptDialog;
